import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getAllVideos, paginate, searchVideos } from "@/lib/videos";
import { categories, videos } from "@/mocks/videos";

const hotTags = ["门将失误", "空篮不进", "团战失误", "进站失误", "经典回顾", "VAR", "双误"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const pageParam = Number(searchParams.get("page") || "1") || 1;
  const [input, setInput] = useState(keyword);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  usePageMeta(
    keyword
      ? `搜索“${keyword}” - 51视频每日大赛今日黑榜合集`
      : "全部赛事黑榜视频 - 51视频每日大赛今日黑榜合集",
    keyword
      ? `在 51视频每日大赛今日黑榜合集中搜索“${keyword}”，匹配视频标题与赛事标签，快速定位相关赛事黑榜视频。`
      : "浏览全部赛事黑榜视频条目，按上传时间排序，支持继续使用分类筛选与关键词搜索。",
  );

  const results = keyword ? searchVideos(keyword) : getAllVideos();
  const paged = paginate(results, pageParam, 8);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = input.trim();
    if (!next) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: next });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageChange = (next: number) => {
    if (next <= 1) {
      setSearchParams(keyword ? { q: keyword } : {});
    } else {
      setSearchParams(keyword ? { q: keyword, page: String(next) } : { page: String(next) });
    }
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageShell>
      <section className="border-b border-background-200 bg-background-100">
        <div className="container-page py-7 md:py-9">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }, { label: keyword ? keyword : "全部视频" }]} />
          <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-[30px]">
            {keyword ? (
              <>
                搜索关键词：<span className="text-primary-700">“{keyword}”</span>
              </>
            ) : (
              "浏览全部赛事黑榜视频"
            )}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {keyword
              ? "匹配范围包含视频标题、赛事标签、赛事类型与简介，可按分页查看更多结果。"
              : "尚未输入关键词，当前展示全部收录条目，你可以在下方输入标题或标签进一步缩小范围。"}
          </p>

          <form
            onSubmit={handleSubmit}
            className="mt-5 flex h-12 w-full max-w-2xl items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3"
          >
            <span className="flex h-7 w-7 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-lg" />
            </span>
            <input
              type="search"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入视频标题或赛事标签，例如：门将失误"
              aria-label="搜索视频标题或标签"
              className="h-full min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="btn-primary h-9 shrink-0 px-4 text-sm">
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门标签：</span>
            {hotTags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="chip bg-background-50 text-foreground-600 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="container-page scroll-mt-20 py-8 md:py-10" ref={listRef}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-3">
          <p className="text-sm text-foreground-600">
            共找到 <span className="font-semibold text-foreground-950">{results.length}</span> 条
            {keyword ? `与“${keyword}”相关的视频` : "视频"}
            {results.length > 0 && (
              <span className="ml-2 text-xs text-foreground-400">
                第 {paged.page} / {paged.totalPages} 页
              </span>
            )}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-foreground-600 transition-colors hover:text-primary-700"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>

        {paged.items.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-7 sm:grid-cols-2 lg:grid-cols-3">
              {paged.items.map((video, index) => (
                <div key={video.id} className="anim-fade-up" style={{ animationDelay: `${index * 45}ms` }}>
                  <VideoCard video={video} />
                </div>
              ))}
            </div>
            <Pagination page={paged.page} totalPages={paged.totalPages} onChange={handlePageChange} />
          </>
        ) : (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-search-eye-line" />
            </span>
            <h2 className="mt-4 text-lg font-semibold text-foreground-950">没有找到相关视频</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-foreground-600">
              换个更简短的关键词试试，比如“足球”“门将”“电竞”，或直接从分类入口浏览对应赛事。
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.id}`}
                  className="chip bg-background-100 text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                >
                  <i className={`${category.icon} text-sm text-primary-600`} />
                  {category.name}
                </Link>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/" className="btn-primary h-10 px-5">返回首页</Link>
              <Link to="/search" className="btn-ghost h-10 px-5">浏览全部视频</Link>
            </div>
          </div>
        )}
      </div>

      <section className="border-t border-background-200 bg-background-100">
        <div className="container-page flex flex-col gap-3 py-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-foreground-500">
            当前站点共收录 {videos.length} 条演示视频，覆盖 {categories.length} 个赛事类别。
          </p>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="text-xs text-foreground-600 transition-colors hover:text-primary-700"
              >
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}